/**
 * Data layer for the admin Portfolio Planner.
 *
 *   GET  portfolio_planner/bootstrap/{user_uuid}/  → config, dropdowns, prefill
 *   POST portfolio_planner/generate/               → the PlanResult
 *
 * Same two endpoints, same payloads and same response envelopes the user
 * platform calls (helpers/portfolioPlanner/api.ts there). The only difference
 * is the transport: the admin panel signs requests with the admin bearer token
 * through `userServiceAxiosInstance`, and there is no React Query in this
 * project, so the calls are plain promises the page drives its own loaders off.
 *
 * A prospect (someone with no SMEST account) skips `bootstrap` entirely and
 * only calls `generate`.
 *
 * `generate` is the one heavy call — it reads the fund universe and, with the
 * bond route on, live inventory from the admin backend. Always drive a loading
 * state off it.
 */

import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "./api";
import type {
  PlanResult,
  PlannerBootstrap,
  PlannerBootstrapResponse,
  PlannerGenerateRequest,
  PlannerGenerateResponse,
} from "../Helpers/portfolioPlanner/types";

const payloadOf = (response: any): any => response?.data ?? response;

/** The API answers 200 with a failure envelope; treat that as a failure. */
const assertEnvelope = (response: any, fallback: string): any => {
  const payload = payloadOf(response);
  if (
    payload?.success === false ||
    String(payload?.status || "").trim().toLowerCase() === "failed"
  ) {
    throw new Error(payload?.error || payload?.message || fallback);
  }
  return payload;
};

export const fetchPlannerBootstrap = async (
  userUuid: string
): Promise<PlannerBootstrap> => {
  const payload = assertEnvelope(
    await makeGETRequestOnUserService(
      `/portfolio_planner/bootstrap/${encodeURIComponent(userUuid)}/`
    ),
    "The planner could not be prepared for this investor."
  ) as PlannerBootstrapResponse;
  if (!payload?.data?.config || !payload?.data?.options) {
    throw new Error("The planner configuration was not returned for this investor.");
  }
  return payload.data;
};

/**
 * Takes no user id — everything the plan is built from is in the request. That
 * is what lets a prospect with no SMEST account get a plan and a report: their
 * settings come from Helpers/portfolioPlanner/prospectDefaults instead of
 * `bootstrap`, and nobody's id is used on their behalf.
 */
export const generatePlan = async (
  request: PlannerGenerateRequest
): Promise<PlanResult> => {
  const payload = assertEnvelope(
    await makePOSTRequestOnUserService("/portfolio_planner/generate/", request),
    "The plan could not be generated."
  ) as PlannerGenerateResponse;
  if (!payload?.data?.goals) {
    throw new Error("The plan response did not contain any goals.");
  }
  return payload.data;
};

/**
 * Turns whatever the backend put in an error body into one line worth showing.
 * A 400 is the rulebook rejecting the inputs and is worth reading; a 500 means
 * nothing partial came back and the plan simply has to be re-run.
 */
export const plannerErrorMessage = (error: any): string => {
  const response = error?.response;
  const body = response?.data;
  const fromBody = body?.message || body?.detail || body?.error;
  if (fromBody) return fromBody;
  if (response?.status === 400) {
    return "Add at least one goal with both a cost and a timeline, and give every liability either an interest rate or an EMI.";
  }
  if (response?.status === 401 || response?.status === 403) {
    return "This admin session is not authorized for the planner APIs. Sign in again and retry.";
  }
  if (response?.status === 404) {
    return "The planner has no profile for this investor yet. Complete their MF KYC first.";
  }
  if (response?.status === 500) {
    return "The plan could not be generated. Nothing was saved — adjust an input and try again.";
  }
  if (error?.message) return error.message;
  return "Something went wrong generating the plan. Please try again.";
};
